// Dialog zum Anlegen/Bearbeiten einer Stornobedingung (PRD-020 Thema 3)
//
// Wortgleiche Übertragung von EventTextFormDialog.tsx, OHNE Kategoriefeld (Entscheidung
// 3.1: flache Liste, kein Vorschlagsfeld für Kategorien nötig). Die Umordnung (US-207)
// gilt wortgleich für Stornobedingungen (US-306) und bleibt deshalb erhalten.

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { RichTextEditor } from '@/features/events/RichTextEditor';
import { isBlankMarkdown } from '@/features/events/event-text-pick';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useMe } from '@/hooks/usePermissions';
import { usePartners } from '@/services/event-service';
import { Role, type CancellationTerm, type SaveCancellationTermRequest } from '@/types';

/** Kennwert für „keinem Partner zuordnen" — wortgleich zu EventTextFormDialog. */
const NO_PARTNER = '__zentral__';

interface Props {
  open: boolean;
  /** null = anlegen; sonst bearbeiten. */
  cancellationTerm: CancellationTerm | null;
  /** Serverfehler (409 der Sperre, 404): erscheint im Dialog, nicht als Toast. */
  saveError?: string | null;
  onOpenChange: (open: boolean) => void;
  onSave: (req: SaveCancellationTermRequest) => void;
  isSaving?: boolean;
}

export interface CancellationTermFormValues {
  title: string;
  text: string;
}

const emptyForm = (): CancellationTermFormValues => ({ title: '', text: '' });

/** Die Fehler, die diese Prüfung melden kann — als i18n-Schlüssel. */
export type CancellationTermFormError =
  | 'cancellationTerms.form.errors.title'
  | 'cancellationTerms.form.errors.text';

/**
 * Prüft das Formular und gibt einen i18n-Schlüssel zurück, oder null.
 *
 * isBlankMarkdown statt `trim()`: wortgleich zu EventTextFormDialog, seit der Text
 * ebenfalls im RichTextEditor (Markdown) statt in einer reinen Textarea gepflegt wird.
 */
export function validateCancellationTermForm(
  values: CancellationTermFormValues
): CancellationTermFormError | null {
  if (values.title.trim().length === 0) return 'cancellationTerms.form.errors.title';
  if (isBlankMarkdown(values.text)) return 'cancellationTerms.form.errors.text';

  return null;
}

/**
 * Darf diese Rolle die Partnerzuordnung ändern — und ist das Feld deshalb sichtbar?
 *
 * Wortgleich zu canReassignTemplate (US-207-Muster, übertragen auf Stornobedingungen).
 * Sowohl beim Anlegen als auch beim Ändern sichtbar (29.09.2026 — siehe
 * EventTextFormDialog.tsx's canReassignTemplate für die Begründung).
 */
export function canReassignCancellationTerm(role: string | undefined): boolean {
  return role === Role.Zentral || role === Role.Admin;
}

/**
 * Die Partnerzuordnung fürs Speichern, oder `undefined` wenn das Feld gar nicht
 * angezeigt wurde. Wortgleich zu reassignmentFor.
 */
export function reassignmentFor(
  canReassign: boolean,
  ownerPartnerKey: string
): string | null | undefined {
  if (!canReassign) return undefined;
  return ownerPartnerKey === NO_PARTNER ? null : ownerPartnerKey;
}

export function CancellationTermFormDialog({
  open,
  cancellationTerm,
  saveError,
  onOpenChange,
  onSave,
  isSaving,
}: Props) {
  const { t } = useTranslation('settings');
  const { data: me } = useMe();
  const [form, setForm] = useState<CancellationTermFormValues>(emptyForm);
  const [error, setError] = useState<CancellationTermFormError | null>(null);
  const [ownerPartnerKey, setOwnerPartnerKey] = useState<string>(NO_PARTNER);

  const isEdit = cancellationTerm !== null;
  const canReassign = canReassignCancellationTerm(me?.role);
  const { data: partners = [] } = usePartners(canReassign);

  useEffect(() => {
    if (!open) return;
    setForm(
      cancellationTerm
        ? { title: cancellationTerm.title, text: cancellationTerm.text }
        : emptyForm()
    );
    setOwnerPartnerKey(cancellationTerm?.ownerPartnerKey ?? NO_PARTNER);
    setError(null);
  }, [open, cancellationTerm]);

  const handleSubmit = () => {
    const problem = validateCancellationTermForm(form);
    if (problem) {
      setError(problem);
      return;
    }

    setError(null);
    onSave({
      id: cancellationTerm?.id,
      title: form.title.trim(),
      text: form.text,
      etag: cancellationTerm?.etag,
      ownerPartnerKey: reassignmentFor(canReassign, ownerPartnerKey),
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>
            {t(isEdit ? 'cancellationTerms.form.titleEdit' : 'cancellationTerms.form.titleCreate')}
          </DialogTitle>
          <DialogDescription>{t('cancellationTerms.form.description')}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <label className="text-xs text-muted-foreground" htmlFor="cancellation-term-title">
              {t('cancellationTerms.form.fields.title')}
            </label>
            <Input
              id="cancellation-term-title"
              value={form.title}
              onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
              placeholder="Standard-Stornobedingung"
            />
          </div>

          {/* Nur die Zentrale sieht dieses Feld — wortgleich zu EventTextFormDialog
              (US-207-Muster). */}
          {canReassign && (
            <div className="space-y-1.5">
              <label className="text-xs text-muted-foreground" htmlFor="cancellation-term-owner">
                {t('cancellationTerms.form.fields.owner')}
              </label>
              <Select value={ownerPartnerKey} onValueChange={setOwnerPartnerKey}>
                <SelectTrigger id="cancellation-term-owner">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_PARTNER}>
                    {t('cancellationTerms.form.ownerCentral')}
                  </SelectItem>
                  {partners.map((partner) => (
                    <SelectItem key={partner.id} value={partner.key}>
                      {partner.key}
                      {partner.email ? ` — ${partner.email}` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {t('cancellationTerms.form.ownerHint')}
              </p>
            </div>
          )}

          <div className="space-y-1.5">
            <label className="text-xs text-muted-foreground">
              {t('cancellationTerms.form.fields.text')}
            </label>
            <RichTextEditor
              value={form.text}
              onChange={(markdown) => setForm((prev) => ({ ...prev, text: markdown }))}
            />
          </div>

          {error && <p className="text-sm text-red-600">{t(error)}</p>}
          {!error && saveError && <p className="text-sm text-red-600">{saveError}</p>}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('cancellationTerms.form.cancel')}
          </Button>
          <Button onClick={handleSubmit} disabled={isSaving}>
            {isSaving ? t('cancellationTerms.form.saving') : t('cancellationTerms.form.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
